import IconCopy from '@apollo/icons/small/IconCopy.svg';
import IconHide from '@apollo/icons/small/IconHide.svg';
import { IconButton, Text } from '@apollo/orbit';
import classnames from 'classnames';
import copy from 'copy-to-clipboard';
import React, { useState } from 'react';

import { Tooltip } from '../common/tooltip/Tooltip';

interface Props {
  className?: string;
  firstLineDecorator?: string;
  showLineNumbers?: boolean;
  highlight?: number[];
  /**
   * string that defines how to find text that
   * should appear as "masked out" in the UI (e.g. API keys).
   */
  mask?: string;
  code: string;
  showCopyButton?: boolean;
  title?: string;
}

const maskText = (text: string, mask: string) =>
  text.replace(mask, '•'.repeat(mask.length));

export const Code = ({
  className,
  firstLineDecorator = '',
  code,
  mask,
  highlight = [],
  showLineNumbers = false,
  showCopyButton = false,
  title,
  ...other
}: Props) => {
  const [revealMask, setRevealMask] = useState(false);

  return (
    <pre
      className={classnames(
        className,
        'relative flex flex-col overflow-x-auto rounded border border-primary bg-secondary text-secondary',
      )}
      {...other}
    >
      {(mask || showCopyButton || title) && (
        <div className="flex flex-row items-center justify-end border-b border-b-primary px-2 py-1">
          {title && (
            <Text
              variant="codeSb"
              className="my-auto ml-2 flex-1 place-self-start"
            >
              {title}
            </Text>
          )}
          <div>
            {mask && (
              <Tooltip
                label={
                  revealMask ? 'Hide masked values' : 'Reveal masked values'
                }
              >
                <IconButton
                  onClick={() => {
                    setRevealMask((isRevealed) => !isRevealed);
                  }}
                  type="button"
                  icon={<IconHide />}
                  variant="hidden"
                  aria-label={
                    revealMask ? 'Hide masked values' : 'Reveal masked values'
                  }
                  data-testid="reveal-mask"
                  size="sm"
                />
              </Tooltip>
            )}
            {showCopyButton && (
              <Tooltip
                label="Copy"
                clickedLabel="Copied"
                data-chromatic="ignore"
              >
                <IconButton
                  onClick={() => {
                    copy(code);
                  }}
                  type="button"
                  icon={<IconCopy />}
                  variant="hidden"
                  aria-label="Copy"
                  size="sm"
                />
              </Tooltip>
            )}
          </div>
        </div>
      )}
      <code
        className={classnames(
          'grid flex-1 whitespace-pre-wrap p-4 font-mono text-sm text-primary',
          showLineNumbers ? 'auto-cols-[min-content_auto]' : 'auto-cols-auto',
        )}
      >
        {code.split('\n').map((line, i) => (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={`code-${i}`}
            className={classnames(
              '-mr-4 h-min flex-1 break-all pr-4',
              !showLineNumbers && '-ml-4 pl-4',
              {
                'bg-highlight': highlight.includes(i + 1),
                'text-neutral': line.startsWith('//'),
              },
            )}
            style={{
              gridColumn: showLineNumbers ? 2 : 1,
              gridRow: i + 1,
            }}
          >
            {i === 0 && firstLineDecorator}
            {mask && !revealMask ? maskText(line, mask) : line}
          </div>
        ))}
        {showLineNumbers && (
          <>
            {code.split('\n').map((_x, i) => (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={`number-${i}`}
                className={classnames(
                  '-ml-4 flex-none px-4',
                  highlight.includes(i + 1)
                    ? 'bg-primary text-primary'
                    : 'bg-secondary text-secondary',
                )}
                style={{
                  gridColumn: 1,
                  gridRow: i + 1,
                }}
              >
                {i + 1}
              </div>
            ))}
          </>
        )}
      </code>
    </pre>
  );
};
